import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useToast } from "Components/Common/Toast/ToastContext";
import { downloadDocumentBlob } from "services/projectService";
import { triggerBlobDownload } from "utils/Common/fileDownload";
import { useState } from "react";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import NoProgressReports from "Assets/Img/NoProgressReports";
import Button from "Components/Common/Button/Button";

interface ProgressReport {
  id: string;
  reportNumber: number;
  reportDate: string;
  drawNumber: number;
  inspectorName: string;
  photoCount: number;
  recommendation: string;
  recommendationText?: string;
}

interface Props {
  reports: ProgressReport[];
  loading: boolean;
  bankName: string;
  projectId: string;
}

const REC_MAP: Record<string, { cls: string; icon: string }> = {
  hold: { cls: "hold", icon: "⚠" },
  approve: { cls: "pay", icon: "✓" },
  reject: { cls: "reject", icon: "✕" },
};

export default function OwnerProgressReportsSection({
  reports,
  loading,
  bankName,
  projectId,
}: Props) {
  const showToast = useToast();
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleViewReport = async (reportId: string) => {
    setViewingId(reportId);
    try {
      const blob = await downloadDocumentBlob(projectId, reportId, true);
      if (blob) {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } else {
        showToast("Report not found.");
      }
    } catch {
      showToast("Failed to open report.");
    } finally {
      setViewingId(null);
    }
  };

  const handleDownloadPDF = async (reportId: string, reportNumber: number) => {
    setDownloadingId(reportId);
    try {
      const blob = await downloadDocumentBlob(projectId, reportId, true);
      if (blob) {
        triggerBlobDownload(
          blob,
          `Report_${String(reportNumber).padStart(2, "0")}.pdf`,
        );
      } else {
        showToast("Report not found.");
      }
    } catch {
      showToast("Failed to download report.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
      <div className="section-title">Construction Progress Reports</div>
      <div className="section-sub">
        Reports prepared by CSC New England and submitted to {bankName} after
        each field inspection
      </div>

      <div className="card">
        <div className="card-title">
          All Reports <span className="read-only-badge">Read Only</span>
        </div>

        {loading ? (
          [1, 2, 3].map((i) => (
            <div className="report-item" key={i}>
              <div className="ri-left">
                <Skeleton width={220} height={13} className="ri-skel-line1" />
                <Skeleton width={280} height={11} className="ri-skel-line2" />
                <Skeleton width={340} height={11} />
              </div>
              <div className="ri-actions skel">
                <Skeleton width={52} height={30} borderRadius={6} />
                <Skeleton width={52} height={30} borderRadius={6} />
              </div>
            </div>
          ))
        ) : reports.length === 0 ? (
          <EmptyState
            icon={<NoProgressReports />}
            title="No Progress Reports Yet"
            subtitle="Progress reports will appear here once inspections are completed and submitted."
          />
        ) : (
          reports.map((report) => {
            const { cls: recClass, icon: recIcon } = REC_MAP[
              report.recommendation
            ] ?? { cls: "pay", icon: "✓" };
            return (
              <div className="report-item" key={report.id}>
                <div className="ri-left">
                  <div className="ri-title">
                    Report {String(report.reportNumber).padStart(2, "0")} —{" "}
                    {new Date(report.reportDate).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <div className="ri-meta">
                    Draw #{report.drawNumber} · Inspector:{" "}
                    {report.inspectorName} · {report.photoCount} photos
                  </div>
                  {report.recommendationText && (
                    <div className={`ri-rec ${recClass}`}>
                      {recIcon} CSC Recommendation: {report.recommendationText}
                    </div>
                  )}
                </div>
                <div className="ri-actions">
                  <Button
                    className="btn btn-s btn-sm"
                    onClick={() => handleViewReport(report.id)}
                    disabled={
                      viewingId === report.id || downloadingId === report.id
                    }
                  >
                    {viewingId === report.id ? (
                      <>
                        <span className="btn-spinner" /> Opening…
                      </>
                    ) : (
                      "View"
                    )}
                  </Button>
                  <Button
                    className="btn btn-s btn-sm"
                    onClick={() =>
                      handleDownloadPDF(report.id, report.reportNumber)
                    }
                    disabled={
                      downloadingId === report.id || viewingId === report.id
                    }
                  >
                    {downloadingId === report.id ? (
                      <>
                        <span className="btn-spinner" /> Downloading…
                      </>
                    ) : (
                      "PDF"
                    )}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
